import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import {
  DEPARTMENTS, AGENT_ESCALATION, AGENT_FUNCTION_DEPTS, LEVEL_BANDS, formatAmount, formatAmountCompact, deslugify, slugify,
  API_V1, authFetch,
} from '../constants';
import KpiCard from '../components/ui/KpiCard';

// Department name -> CSS variable color
const DEPT_COLOR_VAR: Record<string, string> = {
  'CFO Office': 'var(--dept-cfo)', 'Finance Operations': 'var(--dept-finops)',
  'FP&A': 'var(--dept-fpa)', 'Accounting': 'var(--dept-accounting)',
  'Treasury': 'var(--dept-treasury)', 'Tax': 'var(--dept-tax)',
  'Credit & Risk': 'var(--dept-credit)', 'Financial Systems': 'var(--dept-finsys)',
  'Internal Audit': 'var(--dept-audit)', 'Payroll': 'var(--dept-payroll)',
  'Procurement': 'var(--dept-procurement)',
};

// Level display config
const LV_CFG: Record<string, { name: string; color: string }> = {
  L6: { name: 'Executive', color: 'var(--navy-800)' },
  L5: { name: 'Director', color: 'var(--navy-800)' },
  L4: { name: 'Manager', color: '#b45309' },
  L3: { name: 'Supervisor', color: '#6d28d9' },
  L2: { name: 'Analyst', color: '#059669' },
};

function healthColor(h: number): string {
  return h >= 70 ? 'var(--green)' : h >= 50 ? 'var(--amber)' : 'var(--red)';
}

export default function DepartmentView() {
  const { department } = useParams<{ department: string }>();
  const navigate = useNavigate();
  const { getDeptStats, getAgentsForDept, getExceptionsForDept, filteredExceptions, loading, role } = useApp();

  const [expandedSkills, setExpandedSkills] = useState<string | null>(null);
  const [liveDOA, setLiveDOA] = useState<Record<string, number> | null>(null);

  // Fetch live DOA levels from org_config (overrides hardcoded constants)
  useEffect(() => {
    authFetch(`${API_V1}/org/configuration/doa-levels`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setLiveDOA(data); })
      .catch(() => {});
  }, []);

  // Build live-aware escalation lookup: if org has DOA config, use it
  const getLiveLimit = (agentId: string): number => {
    const esc = AGENT_ESCALATION[agentId] || { level: 'L2', limit: 50000 };
    if (liveDOA && liveDOA[esc.level] != null) return liveDOA[esc.level];
    return esc.limit;
  };

  const deptName = deslugify(department || '');
  const deptConfig = DEPARTMENTS[deptName];

  if (!deptConfig) {
    return (
      <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-secondary)' }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>Department Not Found</div>
        <div style={{ fontSize: 13, marginBottom: 24 }}>"{department}" is not a valid department.</div>
        <button onClick={() => navigate('/')} style={{ padding: '10px 24px', background: 'var(--accent-subtle)', border: '1px solid var(--accent-border)', borderRadius: 8, color: 'var(--navy-700)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Back to Command Center
        </button>
      </div>
    );
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 80, color: 'var(--text-secondary)' }}>Loading...</div>;

  const stats = getDeptStats(deptName);
  const agents = getAgentsForDept(deptName);
  const deptExceptions = getExceptionsForDept(deptName);
  const color = DEPT_COLOR_VAR[deptName] || deptConfig.color;
  const h = stats.health;

  // Compute per-agent workload (includes cross-dept escalations)
  const getAgentWorkload = (agentId: string) => {
    const esc = AGENT_ESCALATION[agentId] || { level: 'L2', limit: 50000 };
    const band = LEVEL_BANDS[esc.level] || { floor: 0, ceiling: 50000 };
    const agentDepts = AGENT_FUNCTION_DEPTS[agentId];
    const RESOLVED = ['RESOLVED_AUTO', 'RESOLVED_HUMAN', 'DISMISSED'];
    // Include cross-dept escalated exceptions for this agent's level
    const deptIds = new Set(deptExceptions.map((e: any) => e.id));
    const crossDeptEscalated = filteredExceptions.filter(e =>
      e.escalation_level === esc.level && !deptIds.has(e.id)
    );
    const crossDeptIds = new Set(crossDeptEscalated.map((e: any) => e.id));
    const allRelevant = [...deptExceptions, ...crossDeptEscalated];
    return allRelevant.filter(e => {
      const status = (e.status || '').toUpperCase();
      if (RESOLVED.includes(status)) return false;
      // Only apply functional dept filter to same-dept exceptions (not cross-dept escalations)
      if (agentDepts && !crossDeptIds.has(e.id) && !agentDepts.includes(e.department)) return false;
      if (e.escalation_level === esc.level) return true;
      if (e.escalation_level && e.escalation_level !== esc.level) return false;
      const amt = e.amount || 0;
      if (esc.level === 'L2') return amt <= band.ceiling;
      return amt > band.floor && amt <= band.ceiling;
    }).length;
  };

  // Group agents by level
  const agentsByLevel: Record<string, any[]> = {};
  for (const agent of agents) {
    const esc = AGENT_ESCALATION[agent.id] || { level: 'L2', limit: 50000 };
    if (!agentsByLevel[esc.level]) agentsByLevel[esc.level] = [];
    agentsByLevel[esc.level].push(agent);
  }
  const levels = ['L6', 'L5', 'L4', 'L3', 'L2'].filter(l => agentsByLevel[l]);

  // Compute standardized KPI values
  const openExceptions = deptExceptions.filter(e => {
    const s = (e.status || '').toUpperCase();
    return ['DETECTED', 'QUEUED', 'PROCESSING', 'FLAGGED', 'PENDING_HUMAN', 'ESCALATED', 'UNDER_INVESTIGATION'].includes(s);
  });
  const pendingReview = deptExceptions.filter(e => {
    const s = (e.status || '').toUpperCase();
    return ['FLAGGED', 'PENDING_HUMAN'].includes(s);
  });
  const confirmedExc = deptExceptions.filter(e => {
    const s = (e.status || '').toUpperCase();
    return s.startsWith('RESOLVED');
  });
  const escalatedExc = deptExceptions.filter(e => (e.status || '').toUpperCase() === 'ESCALATED');

  const sumAmt = (arr: any[]) => arr.reduce((s: number, e: any) => s + Math.abs(e.amount || 0), 0);

  const deptSlug = slugify(deptName);

  // Role-based access note
  const roleAccess = role.id === 'cfo' ? 'Full access (all departments)'
    : role.id === 'vp_finance' ? 'Division-scoped access'
    : role.id === 'controller' ? 'Entity-scoped access'
    : role.id === 'treasury_mgr' ? 'Department-scoped access'
    : role.id === 'ap_supervisor' ? 'Department-scoped access'
    : role.id === 'analyst' ? 'Task-level access'
    : 'View-only access';

  return (
    <div>
      {/* Breadcrumb */}
      <div className="bc">
        <span className="bc-link" onClick={() => navigate('/')}>Organization</span>
        <span className="bc-sep">&#9658;</span>
        <span className="bc-cur">{deptName}</span>
      </div>

      {/* Department Header Card */}
      <div className="card" style={{ borderLeft: `3px solid ${color}`, padding: '20px 24px', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <div>
            <div style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 4 }}>{deptName}</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              {agents.length} agents &middot; {openExceptions.length.toLocaleString()} open of {stats.exceptions.toLocaleString()} total &middot; {formatAmount(sumAmt(openExceptions))} exposure
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginTop: 4 }}>{roleAccess}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="mono" style={{ fontSize: '1.5rem', fontWeight: 700, color: healthColor(h) }}>{h}%</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>Health Score</div>
          </div>
        </div>
        <div style={{ height: 6, background: 'var(--bg-elevated)', borderRadius: 3, overflow: 'hidden', marginTop: 8 }}>
          <div style={{ height: '100%', width: `${h}%`, background: healthColor(h), borderRadius: 3 }} />
        </div>
      </div>

      {/* 5 KPI boxes — standardized metrics, clickable */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 24 }}>
        <KpiCard compact
          label="Open Exceptions"
          value={openExceptions.length.toLocaleString()}
          deltaLabel={`${stats.exceptions.toLocaleString()} total`}
          valueColor="var(--accent)"
          onClick={() => navigate(`/exception-tracker?dept=${deptSlug}`)}
        />
        <KpiCard compact
          label="Open Exposure"
          value={formatAmountCompact(sumAmt(openExceptions))}
          deltaLabel={`${openExceptions.length} open`}
          valueColor="var(--warning)"
          onClick={() => navigate(`/exception-tracker?dept=${deptSlug}&status=open`)}
        />
        <KpiCard compact
          label="Pending Review"
          value={formatAmountCompact(sumAmt(pendingReview))}
          deltaLabel={`${pendingReview.length} pending`}
          valueColor="var(--negative)"
          onClick={() => navigate(`/exception-tracker?dept=${deptSlug}&status=flagged`)}
        />
        <KpiCard compact
          label="Confirmed"
          value={formatAmountCompact(sumAmt(confirmedExc))}
          deltaLabel={`${confirmedExc.length} resolved`}
          valueColor="var(--positive)"
          onClick={() => navigate(`/exception-tracker?dept=${deptSlug}&status=resolved`)}
        />
        <KpiCard compact
          label="Escalated"
          value={formatAmountCompact(sumAmt(escalatedExc))}
          deltaLabel={`${escalatedExc.length} escalated`}
          valueColor="var(--warning)"
          onClick={() => navigate(`/exception-tracker?dept=${deptSlug}&status=escalated`)}
        />
      </div>

      {/* Agent Hierarchy */}
      <div className="stitle">Agent Hierarchy</div>

      {agents.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          No agents registered for {deptName}
        </div>
      ) : (
        levels.map(level => {
          const lv = LV_CFG[level] || { name: level, color: '#9CA3AF' };
          const levelAgents = agentsByLevel[level];
          const levelLimit = getLiveLimit(levelAgents[0].id);

          return (
            <div key={level} style={{ marginBottom: 20 }}>
              {/* Level header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid var(--border-subtle)' }}>
                <span className="mono" style={{
                  fontSize: '0.7rem', fontWeight: 700, padding: '3px 8px', borderRadius: 4,
                  background: lv.color, color: 'white',
                }}>
                  {level}
                </span>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: lv.color }}>{lv.name}</span>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                  Approval up to {levelLimit >= 1e15 ? 'Unlimited' : formatAmount(levelLimit)}
                </span>
              </div>

              {/* Agent cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
                {levelAgents.map((agent: any) => {
                  const modeColor = agent.default_mode === 'auto' ? 'var(--green)' : agent.default_mode === 'human_decision' ? 'var(--red)' : 'var(--amber)';
                  const modeLabel = agent.default_mode === 'auto' ? 'Auto' : agent.default_mode === 'human_decision' ? 'Human' : 'Hybrid';
                  const workload = getAgentWorkload(agent.id);
                  const isActive = workload > 0;
                  const isSkillsExpanded = expandedSkills === agent.id;
                  const skillNames: string[] = agent.skills || [];

                  return (
                    <div
                      key={agent.id}
                      className="card card-click"
                      style={{ padding: '16px 18px' }}
                      onClick={() => navigate(`/department/${deptSlug}/${agent.id}`)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                        {/* Avatar circle with initial */}
                        <div style={{
                          width: 36, height: 36, borderRadius: '50%',
                          background: 'var(--bg-elevated)', border: `2px solid ${lv.color}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.8rem', fontWeight: 600,
                        }}>
                          {(agent.title || agent.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{agent.name || agent.title}</div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{agent.title !== agent.name ? agent.title : agent.department}</div>
                        </div>
                        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: isActive ? 'var(--green)' : 'var(--text-tertiary)' }}>
                          <span style={{ animation: isActive ? 'pulse 2s infinite' : 'none' }}>&#9679;</span> {isActive ? 'Active' : 'Idle'}
                        </div>
                      </div>

                      {/* 3 metric boxes */}
                      <div style={{ display: 'flex', gap: 10 }} onClick={e => e.stopPropagation()}>
                        <div
                          onClick={() => setExpandedSkills(isSkillsExpanded ? null : agent.id)}
                          style={{ background: 'var(--bg-elevated)', borderRadius: 6, padding: '8px 14px', textAlign: 'center', flex: 1, cursor: 'pointer' }}
                        >
                          <div className="mono" style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--accent)' }}>{agent.skills_count || skillNames.length}</div>
                          <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 500, marginTop: 2 }}>Skills</div>
                        </div>
                        <div style={{ background: 'var(--bg-elevated)', borderRadius: 6, padding: '8px 14px', textAlign: 'center', flex: 1 }}>
                          <div className="mono" style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--amber)' }}>{workload}</div>
                          <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 500, marginTop: 2 }}>Workload</div>
                        </div>
                        <div style={{ background: 'var(--bg-elevated)', borderRadius: 6, padding: '8px 14px', textAlign: 'center', flex: 1 }}>
                          <div style={{ fontSize: '0.95rem', fontWeight: 700, color: modeColor }}>{modeLabel}</div>
                          <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 500, marginTop: 2 }}>Mode</div>
                        </div>
                      </div>

                      {/* Expanded skills */}
                      {isSkillsExpanded && skillNames.length > 0 && (
                        <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--accent-subtle)', borderRadius: 8, border: '1px solid var(--accent-border)' }}>
                          <div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--text-tertiary)', letterSpacing: '0.05em', marginBottom: 6, textTransform: 'uppercase' }}>Skills</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {skillNames.map((sk: string) => (
                              <span key={sk} style={{ fontSize: '0.65rem', padding: '2px 8px', background: 'rgba(0,0,0,0.04)', borderRadius: 4, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                                {sk.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

